import { App, Button, Flex, Modal, notification, Space, Spin, Steps, TableProps, Typography } from "antd";
import { FC, Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaleOrderItemState, SaleItemState } from "@/types/sales";
import { AcceptStateEnum, PurchaseOrder, PurchaseOrderAdd, PurchaseOrderAddItem, PurchaseOrderItem, PurchaseOrderStateEnum, Supplier, SupplierPrice } from "@/types/purchase";
import SText from "@/styles/SText";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";
import { numFormmater } from "@/pages/Requirement/util";
import SelectSupplier from "@/components/Selector/SelectSupplier";
import { SConModal } from "@/styles/SConModal";
import { productList, ProductListObjParams } from "@/apis/product";
import { supplierPricesFetch } from "@/apis/supplier";
import { add, itemUpdateByKey, orderUpdateByKey } from "@/apis/purchase";
import { divided, multi, noRateAmount } from "@/utils/calc";
import { getSuppLadderPriceData } from "@/utils/getSuppLadderPrice";
import { updateByKey } from "@/apis/order";
import { priceFormat } from "@/utils/format";
import { CurrencySelect } from "@/components/Selector";
import { BASEURL } from "@/apis";

interface PurSwapModalProps {
  open: boolean;
  saleOrders: SaleOrderItemState[];
  onClose: () => void;
  onSwapSuccess: () => void;
}

const PurSwapModal: FC<PurSwapModalProps> = ({
  open,
  saleOrders,
  onClose,
  onSwapSuccess
}) => {
  const { t } = useTranslation();

  // 步骤状态
  const [current, setCurrent] = useState<number>(0);

  // 供应商相关状态
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  const [resPurOrder, setResPurOrder] = useState<PurchaseOrder | null>(null);

  // 加载状态
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [errCodes, setErrCodes] = useState<Array<string>>([]);

  // 客户相关状态
  const { customer, customerName, allItems } = useMemo(() => {
    let customerName: string = '';
    if (!!saleOrders[0]?.customer.shortName && !!saleOrders[0]?.customer.code) customerName = `${saleOrders[0]?.customer.shortName} (${saleOrders[0]?.customer.code}) `
    else customerName = saleOrders[0]?.customer.shortName || saleOrders[0]?.customer.code;
    let allItems: Array<SaleItemState> = [];
    saleOrders.forEach(order => {
      allItems.push(
        ...order.items.map(item => ({
          ...item,
          order: { ...order, items: [] }
        }))
      )
    })
    return {
      customer: saleOrders[0]?.customer!,
      customerName,
      allItems: allItems.filter(item => !!item.quantity || !!item.bquantity)
    }
  }, [saleOrders]);

  //采购单
  const { purItems, total } = useMemo(() => {
    let purItems: PurchaseOrderItem[] = [];
    let total: number = 0;
    if (!!resPurOrder?.items) {
      let order: PurchaseOrder = {
        ...resPurOrder,
        items: null
      };
      total = resPurOrder.items.length;
      resPurOrder.items.forEach(item => {
        purItems.push({
          ...item,
          order
        });
      })
    }
    return { purItems, total };
  }, [resPurOrder])

  // 关闭弹窗
  const handleClose = () => {
    onClose();
  };

  // 下一步
  const handleNext = async () => {
    if (!!!allItems.find(item => !!item.quantity || !!item.bquantity)) {
      notification.error({
        message: t("requirement.noDataToTransfer"),
        placement: "top"
      });
      return;
    }

    if (!!!supplier) {
      notification.error({
        message: t("requirement.selectSupplierFirst"),
        placement: "top"
      });
      return;
    }
    // 匹配数据，开始转单

    setSubmitLoading(true);
    setErrCodes([]);
    try {
      let codes = allItems.map(i => i.product!.code);
      const params: ProductListObjParams = {
        categoryId: undefined,
        params: {
          strs: codes,
        },
        wmsApi: customer.wmsApi
      };
      const { body: customerProducts } = await productList(params);
      let errProductCodes: string[] = [];
      codes.forEach(code => {
        if (!!!customerProducts.find(p => p.code === code)) errProductCodes.push(code)
      });
      if (errProductCodes.length > 0) {
        notification.error({ message: t('sale.notCusPros') });
        setErrCodes([...errProductCodes]);
        setSubmitLoading(false);
        return;
      };
      const { body: supplierPrice } = await supplierPricesFetch(supplier.id, undefined, customer.wmsApi);
      const supplierPriceMap: Record<string, SupplierPrice> = {};
      supplierPrice.forEach(price => supplierPriceMap[price.product!.code] = price)
      codes.forEach(code => {
        if (!!!supplierPriceMap[code]) errProductCodes.push(code)
      });
      if (errProductCodes.length > 0) {
        notification.error({ message: t('purchase.swapErr'), description: t('sale.notPurProPrice') });
        setErrCodes([...errProductCodes]);
        setSubmitLoading(false);
        return;
      };
      //添加采购单
      const addItems: PurchaseOrderAddItem[] = [];
      allItems.forEach(item => {
        const count = Number(item.quantity || 0) + Number(item.bquantity || 0);
        const rate = divided(item?.order?.taxRate ?? '0', '100');
        const supplierPrice = supplierPriceMap[item.product!.code];
        const rateAmountInfo = multi(count.toString(), supplierPrice.price);
        const amountInfo = noRateAmount(rateAmountInfo, rate); // 商品未税总价
        let ladderPrice = getSuppLadderPriceData(supplierPrice, count);
        addItems.push({
          type: 0,
          sourceId: '',
          priceId: ladderPrice?.id ?? '',
          count,
          amount: amountInfo,
          rateAmount: rateAmountInfo,
          remark: '',
          scheduledOrderTime: null,
          deliveryDeadline: null,
          deliveredQuantity: 0,
          estimateArrivalAt: null,
          taxRate: item?.order?.taxRate,
          orderState: PurchaseOrderStateEnum.PAY_PENDING,
          otherNumber1: item.order.number,
        })
      })
      const addInfo: PurchaseOrderAdd = {
        currency: saleOrders[0].currency,
        payMethod: '',
        remark: '',
        receiver: saleOrders[0].receiver,
        receiverAddress: saleOrders[0].receiverAddress,
        receiverContact: saleOrders[0].receiverContact,
        receiverMobile: saleOrders[0].receiverMobile,
        amount: "0",
        rateAmount: "0",
        supplierId: supplier.id,
        items: addItems,
        acceptState: AcceptStateEnum.RESOLVED,
        sourceOrder: saleOrders.map(o => o.number).join(','),
        sourceServer: BASEURL
      };
      let purOrder = await add(addInfo);

      await orderUpdateByKey<AcceptStateEnum>(purOrder.id, { key: "acceptState", value: AcceptStateEnum.RESOLVED })

      //更新关联字段
      try {
        for (let i = 0; i < purOrder!.items!.length; i++) {
          await itemUpdateByKey(purOrder?.items?.[i]?.id ?? '', { key: "orderState", value: PurchaseOrderStateEnum.PAYED })
        }
        for (let k = 0; k < saleOrders.length; k++) {
          const saleOrderRow = saleOrders[k];
          let purNosStr = purOrder.items?.filter(item => item.otherNumber1.includes(saleOrderRow.number)).map(item => item.number).join(',');
          await updateByKey(saleOrderRow.id, { key: "otherNumber1", value: !!saleOrderRow.otherNumber1 ? `${saleOrderRow.otherNumber1},${purNosStr}` : purNosStr })
        }
      } catch (error) { }
      notification.success({
        message: t("purchase.purchaseTransferSuccess"),
        description: `${purOrder.items?.map(item => item.number)?.join(',') ?? ''}`,
      })
      setResPurOrder({ ...purOrder, acceptState: AcceptStateEnum.RESOLVED });
      setCurrent(current + 1);
      setSubmitLoading(false);
    } catch (error) {
      notification.error({
        message: t('purchase.swapErr')
      })
      setSubmitLoading(false);
    }
  };


  const columns: TableProps<SaleItemState>['columns'] = useMemo(() => [
    {
      title: <SText text={t("requirement.saleOrderNumber")} />,
      width: 180,
      render: (_, item) => <SText text={item.order?.number} style={{ width: "100%", color: errCodes.includes(item.product!.code) ? "red" : "#000" }} />,
    },
    {
      title: <SText text={t("product.code")} />,
      dataIndex: ["product", "code"],
      width: 160,
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%", color: errCodes.includes(item.product!.code) ? "red" : "#000" }} />,
    },
    {
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 100,
      render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("order.brand")} />,
      dataIndex: "model1",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      width: 80,
      render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("purchase.purCount")} />,
      dataIndex: "count",
      width: 80,
      render: (_, item) => (
        <SText text={numFormmater((item?.quantity || 0) + (item.bquantity || 0))} style={{ width: "100%" }} />
      ),
    },
  ], [errCodes]);

  const purCols: TableProps<PurchaseOrderItem>['columns'] = [
    {
      title: <SText text={t("purchase.purNo")} />,
      width: 140,
      fixed: "left",
      render: (_, item) => <SText text={item?.number} style={{ width: "100%", color: errCodes.includes(item.product!.code) ? "red" : "#000" }} />,
    },
    {
      title: <SText text={t("product.code")} />,
      dataIndex: ["product", "code"],
      width: 100,
      render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%", color: errCodes.includes(item.product!.code) ? "red" : "#000" }} />,
    },
    {
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 100,
      render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("order.brand")} />,
      dataIndex: "model1",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      width: 80,
      render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("purchase.count")} />,
      dataIndex: "count",
      width: 80,
      render: (_, item) => (
        <SText text={numFormmater(item?.count || 0)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.code")} />,
      dataIndex: ["order", "supplier", "code"],
      width: 80,
      render: (_, item) => <SText text={item?.order?.supplier?.code} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("purchase.shortName")} />,
      dataIndex: "name",
      width: 180,
      // fixed: 'left',
      render: (_, item) => (
        <SText text={item?.order?.supplier?.shortName ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.amount")} />,
      key: "amount",
      width: 80,
      render: (_, item) => {
        const taxRate = item.quotationRequest?.price?.taxRate || item.taxRate;
        if (taxRate && item.rateAmount) {
          const rate = divided(taxRate ?? '0', '100')
          const amountPtrice = divided(item.rateAmount, (1 + Number(rate)).toString())

          return <SText text={numFormmater(Number(amountPtrice))} style={{ width: "100%" }} />
        }
        else return '-'
      },
    },
    {
      title: <SText text={t("purchase.rateAmount")} />,
      dataIndex: "rateAmount",
      width: 80,
      render: (_, item) => (
        <SText text={priceFormat(item?.rateAmount)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 100,
      render: (_, item) => <CurrencySelect value={item?.order?.currency} readOnly />
    },
  ]
  // 步骤配置
  const stepsItems = useMemo(() => [
    {
      title: t("sale.purchaseData"),
      content: (
        <STableLayout
          total={allItems.length}
          dataSource={allItems}
          columns={columns as Array<AnyObject>}
          headerChildren={null}
          scrollX={1000}
          extHeight={70}
        />
      ),
    },
    {
      title: t("requirement.complete"),
      content: (
        <STableLayout
          dataSource={purItems}
          total={total}
          headerChildren={
            <Typography.Title level={4} style={{ margin: 0 }} >{t('warehouse.purchaseOrder')}</Typography.Title>
          }
          extHeight={80}
          columns={purCols as Array<AnyObject>}
          scrollX={1000}
          pagination={false}
        />
      ),
    }
  ], [supplier, allItems, errCodes, resPurOrder, t]);

  // Modal footer
  const footer = (
    <Flex justify="space-between">
      <Space>
        {current === 0 && (
          <SConModal disable={submitLoading} title={t('common.tip')} content={t('sale.confirmGeneratePurOrder')} onOk={handleNext} >
            <Button disabled={submitLoading || allItems.length === 0} type='primary' >{t('purchase.startSwap')}</Button>
          </SConModal>
        )}
      </Space>
      <Space>
        <Button disabled={submitLoading} onClick={handleClose}>{t("requirement.close")}</Button>
      </Space>
    </Flex>
  );

  return (
    <Modal
      title={t("requirement.swapSale")}
      open={open} centered
      onCancel={handleClose}
      width={1200} footer={footer}
      maskClosable={false}
    >
      <Spin spinning={submitLoading}>
        <Flex vertical style={{ height: "70vh" }} gap={8}>
          <Steps current={current} items={stepsItems} />
          {current === 0 && <Flex gap={20} justify='space-between' >
            <Typography.Title level={4} style={{ marginTop: 8 }}>{t("sale.shortName")}: {customerName}</Typography.Title>
            <Space >
              <Typography.Title level={5} style={{ marginTop: 8 }}>{t("common.supplier")}: </Typography.Title>
              <SelectSupplier style={{ width: 220 }} onChangeSupplier={setSupplier} />
            </Space>
          </Flex>}
          {stepsItems[current].content}
        </Flex>
      </Spin>
    </Modal>
  );
};

export default PurSwapModal;